import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBrandsQuery } from '@/data/brands.queries';
import { useCategoriesQuery } from '@/data/categories.queries';
import { useSourceMarketsQuery } from '@/data/source-markets.queries';
import type { Product } from '@/data/products';
import type { ProductListParams, ProductListResponse } from '@/data/products.api';
import { useProductsQuery } from '@/data/products.queries';
import type { CatalogFilters } from './catalog.types';

const normalizeCategoryKey = (value: string | null | undefined) =>
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

const parseCategoryKeys = (value: string | null | undefined) =>
  Array.from(
    new Set(
      (value ?? '')
        .split(',')
        .map((category) => normalizeCategoryKey(category))
        .filter(Boolean)
    )
  );

const formatSlugLabel = (value: string | null) =>
  (value ?? '')
    .split('-')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

export function useCatalogState(language: 'mn' | 'en') {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search')?.toLowerCase() || '';
  const filterFromUrl = normalizeCategoryKey(searchParams.get('filter')) || null;
  const categoryParam = searchParams.get('category');
  const categoriesFromUrl = useMemo(() => parseCategoryKeys(categoryParam), [categoryParam]);
  const brandFromUrl = normalizeCategoryKey(searchParams.get('brand')) || null;
  const sourceMarketFromUrl = normalizeCategoryKey(searchParams.get('store')) || null;
  const saleFromUrl = /^(true|1|yes)$/i.test(searchParams.get('sale') || '');
  const pageFromUrl = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);

  const { data: categories = [] } = useCategoriesQuery();
  const { data: brands = [] } = useBrandsQuery();
  const { data: sourceMarkets = [] } = useSourceMarketsQuery();

  const validCategorySlugs = useMemo(
    () =>
      new Set(
        categories
          .map((category) => normalizeCategoryKey(category.slug))
          .filter((slug): slug is string => Boolean(slug))
      ),
    [categories]
  );

  const activeCategoriesFromUrl = useMemo(() => {
    if (categoriesFromUrl.length === 0) return [];
    if (validCategorySlugs.size === 0) return categoriesFromUrl;
    return categoriesFromUrl.filter((category) => validCategorySlugs.has(category));
  }, [categoriesFromUrl, validCategorySlugs]);

  const [sortBy, setSortBy] = useState<'newest' | 'price-low' | 'price-high'>('newest');
  const [isPriceRangeTouched, setIsPriceRangeTouched] = useState(false);
  const [filters, setFilters] = useState<CatalogFilters>({
    priceRange: [0, 1000],
    sizes: [],
  });

  const productQueryParams = useMemo<ProductListParams>(
    () => ({
      page: pageFromUrl,
      limit: 24,
      categorySlugs: activeCategoriesFromUrl,
      brandSlug: brandFromUrl,
      sourceMarket: sourceMarketFromUrl,
      search: searchQuery || null,
      sort: sortBy,
      sizes: filters.sizes,
      onSale: saleFromUrl,
    }),
    [pageFromUrl, activeCategoriesFromUrl, brandFromUrl, sourceMarketFromUrl, searchQuery, sortBy, filters.sizes, saleFromUrl]
  );

  const productsQuery = useProductsQuery(productQueryParams);
  const products = productsQuery.data?.products ?? [];
  const pagination = productsQuery.data?.pagination;

  const maxCatalogPrice = useMemo(() => {
    const highestFromBounds = Number.isFinite(productsQuery.data?.priceBounds?.max)
      ? productsQuery.data?.priceBounds?.max ?? 0
      : 0;
    const highestFromProducts = products.reduce((highest, product) => {
      const variantPrices = product.variants.map((variant) => Number(variant.price) || 0);
      return Math.max(highest, Number(product.price) || 0, ...variantPrices);
    }, 0);
    const highest = Math.max(highestFromBounds, highestFromProducts);
    return Math.max(1000, Math.ceil(highest / 1000) * 1000);
  }, [products, productsQuery.data?.priceBounds?.max]);

  const effectivePriceRange = useMemo<[number, number]>(() => {
    const [minPrice, maxPrice] = filters.priceRange;
    if (!isPriceRangeTouched && maxPrice < maxCatalogPrice) {
      return [minPrice, maxCatalogPrice];
    }

    return filters.priceRange;
  }, [filters.priceRange, isPriceRangeTouched, maxCatalogPrice]);

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        const matchesPrice =
          product.price >= effectivePriceRange[0] && product.price <= effectivePriceRange[1];
        return matchesPrice;
      }),
    [products, effectivePriceRange]
  );

  const areSameFilters = useCallback((left: CatalogFilters, right: CatalogFilters) => {
    if (left.priceRange[0] !== right.priceRange[0] || left.priceRange[1] !== right.priceRange[1]) {
      return false;
    }

    if (left.sizes.length !== right.sizes.length) {
      return false;
    }

    return left.sizes.every((size, index) => size === right.sizes[index]);
  }, []);

  const handleFilterChange = useCallback((next: CatalogFilters) => {
    setFilters((prev) => (areSameFilters(prev, next) ? prev : next));
  }, [areSameFilters]);

  const resetPaginationForFilterChange = useCallback(() => {
    setSearchParams((currentParams) => {
      if (!currentParams.has('page')) {
        return currentParams;
      }

      const nextParams = new URLSearchParams(currentParams);
      nextParams.delete('page');
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const handleUserPriceChange = useCallback(() => {
    setIsPriceRangeTouched(true);
  }, []);

  const setActiveCategory = useCallback((nextCategory: string) => {
    const normalizedCategory = normalizeCategoryKey(nextCategory);
    if (!normalizedCategory) return;

    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      const currentCategories = parseCategoryKeys(currentParams.get('category'));
      const nextCategories = currentCategories.includes(normalizedCategory)
        ? currentCategories.filter((category) => category !== normalizedCategory)
        : [...currentCategories, normalizedCategory];

      if (nextCategories.length > 0) {
        nextParams.set('category', nextCategories.join(','));
      } else {
        nextParams.delete('category');
      }
      nextParams.delete('page');
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const resolvedBrandLabel = useMemo(() => {
    if (!brandFromUrl) return null;
    const brandMatch = brands.find(
      (brand) => normalizeCategoryKey(brand.slug) === brandFromUrl
    );
    if (brandMatch?.name) return brandMatch.name;
    const productMatch = products.find(
      (product) => normalizeCategoryKey(product.brandSlug) === brandFromUrl
    );
    return productMatch?.brandName || formatSlugLabel(brandFromUrl);
  }, [brandFromUrl, brands, products]);

  const resolvedCategoryLabel = useMemo(() => {
    if (activeCategoriesFromUrl.length === 0) return null;
    const labels = activeCategoriesFromUrl.map((activeCategory) => {
      const categoryMatch = categories.find(
        (category) => normalizeCategoryKey(category.slug) === activeCategory
      );
      if (categoryMatch?.name) return categoryMatch.name;
      const productMatch = products.find(
        (product) => normalizeCategoryKey(product.categorySlug) === activeCategory
      );
      return productMatch?.category || formatSlugLabel(activeCategory);
    });
    return labels.join(' + ');
  }, [activeCategoriesFromUrl, categories, products]);

  const resolvedSourceMarketLabel = useMemo(() => {
    if (!sourceMarketFromUrl) return null;
    const sourceMarketMatch = sourceMarkets.find(
      (sourceMarket) =>
        normalizeCategoryKey(sourceMarket.slug) === sourceMarketFromUrl ||
        normalizeCategoryKey(sourceMarket.code) === sourceMarketFromUrl
    );
    return sourceMarketMatch?.name || formatSlugLabel(sourceMarketFromUrl);
  }, [sourceMarketFromUrl, sourceMarkets]);

  const title = searchQuery
    ? `"${searchQuery}"`
    : resolvedSourceMarketLabel && resolvedBrandLabel && resolvedCategoryLabel
    ? `${resolvedSourceMarketLabel} / ${resolvedBrandLabel} / ${resolvedCategoryLabel}`
    : resolvedSourceMarketLabel && resolvedBrandLabel
    ? `${resolvedSourceMarketLabel} / ${resolvedBrandLabel}`
    : resolvedSourceMarketLabel && resolvedCategoryLabel
    ? `${resolvedSourceMarketLabel} / ${resolvedCategoryLabel}`
    : resolvedSourceMarketLabel
    ? `${resolvedSourceMarketLabel} ${language === 'mn' ? 'бараа' : 'Products'}`
    : resolvedBrandLabel && resolvedCategoryLabel
    ? `${resolvedBrandLabel} / ${resolvedCategoryLabel}`
    : resolvedBrandLabel
    ? `${resolvedBrandLabel} ${language === 'mn' ? 'цуглуулга' : 'Collection'}`
    : saleFromUrl
    ? language === 'mn'
      ? 'Хямдралтай бараа'
      : 'Sale Products'
    : filterFromUrl === 'new'
    ? language === 'mn'
      ? 'Шинэ бараа'
      : 'New Arrivals'
    : language === 'mn' ? 'Манай Бүтээгдэхүүнүүд' : 'Our Products';

  const hasBrandEmptyState =
    Boolean(brandFromUrl) && !productsQuery.isLoading && !productsQuery.error && filteredProducts.length === 0;

  return {
    pageFromUrl,
    searchQuery,
    brandFromUrl,
    sourceMarketFromUrl,
    filterFromUrl,
    sortBy,
    setSortBy,
    filters,
    activeCategories: activeCategoriesFromUrl,
    products,
    filteredProducts,
    pagination,
    maxCatalogPrice,
    title,
    hasBrandEmptyState,
    resolvedBrandLabel,
    resolvedSourceMarketLabel,
    productsQuery,
    handleFilterChange,
    resetPaginationForFilterChange,
    handleUserPriceChange,
    setActiveCategory,
  };
}
